<template>
    <ion-grid class="remove-specific-padding max-height d-flex flex-d-c">
        <logo-row @mousedown="startTimer()" @mouseup="endTimer()" @touchstart="startTimer()" @touchend="endTimer()"/>
        <ion-row v-if="persistentStore.apiUrl !== standardURL">
            <ion-col class="center-x">
                {{ persistentStore.apiUrl }}
            </ion-col>
        </ion-row>

        <ion-row class="no-padding-x-row swiper-row pt-1" style="min-height: 290px;">
            <ion-col>
                <swiper
                    v-if="sliderItems.length"
                    class="custom-pagination start-slider force-equal-height-slides"
                    :class="{'web-slider': !isApp()}"
                    :space-between="8" :pagination="{ clickable: true, type: 'bullets' }"
                    :breakpoints="swiperBreakpointsPromotion" :slideToClickedSlide="true" :centeredSlides="true"
                    :loop="true" :modules="[Pagination]" effect="cards"
                >
                    <template v-for="(promotion, ind) of sliderItems" :key="ind">
                        <swiper-slide>
                            <div class="p-relative slide-card card-border max-size inset-shadow">
                                <img
                                    alt="" :src="promotion.img" class="card-border max-size" style="object-fit: cover;"
                                />
                                <div class="p-absolute fs-22 fw-700 light-light-color ion-text-center slide-card-text">
                                    {{ promotion.text }}
                                </div>
                            </div>
                        </swiper-slide>
                    </template>
                </swiper>
            </ion-col>
        </ion-row>

        <ion-row class="pb-1 flex-grow-2" style="min-height: 80px;">
            <ion-col class="center-y flex-d-c ion-justify-content-end">
                <div class="fs-small">
                    {{ $t('messages.start.notNew') }}?
                </div>
                <button-fix
                    :color="ION_VARIANT.LIGHT" class="coral-coral-color light-light-background light-medium-shadow"
                    @click="$emit('login')"
                >
                    {{ $t('button.login') }}
                </button-fix>
            </ion-col>
        </ion-row>

        <ion-row class="flex-grow-2" style="min-height: 80px;">
            <ion-col class="center-y flex-d-c ion-justify-content-start">
                <div class="fs-small">
                    {{ $t('messages.start.new') }}?
                </div>
                <button-fix :fill="BUTTON_FILL.OUTLINE" class="coral-light-color" @click="$emit('register')">
                    {{ $t('button.register') }}
                </button-fix>
            </ion-col>
        </ion-row>

        <ion-row class="mt-3" style="min-height: 50px;">
            <ion-col class="center-x">
                <language-dropdown class="ml-1 mb-1"/>
            </ion-col>
        </ion-row>
    </ion-grid>
</template>

<script setup lang="ts">
import { alertController, IonCol, IonGrid, IonRow } from "@ionic/vue";
import { computed } from "vue";
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from "swiper/vue";

import LanguageDropdown from "@/components/selects/LanguageDropdown.vue";
import LogoRow from "@/components/LogoRow.vue";
import ButtonFix from "@/components/button/ButtonFix.vue";

import { usePersistentStore } from "@/stores/persistent";

import { useLanguageArray } from "@/composables/useLanguageArray";
import { useApi } from "@/composables/useApi";

import { BUTTON_FILL, ION_VARIANT } from "@/types/enum/ionicEnum";
import { StartScreenInterfaceEmits } from "@/types/emits";

import { swiperBreakpointsPromotion } from "@/assets/variables";

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { isApp } from "@/functions/enviroment";

defineEmits<StartScreenInterfaceEmits>()

const persistentStore = usePersistentStore()
const { changeURL } = useApi()

const initSlides = [
    { text: 'messages.home.image1', img: 'assets/devImages/slider1.png' },
    { text: 'messages.home.image2', img: 'assets/devImages/slide2.svg' },
    { text: 'messages.home.image3', img: 'assets/devImages/slider3.png' },
]

// mobile view → 6 slides
const slidesArray = [...initSlides, ...initSlides];
// desctop view → 9 slides
if (window.innerWidth > 1920) slidesArray.push(...initSlides)

const articlePromotions = useLanguageArray(slidesArray, 'text')

const standardURL = process.env.VUE_APP_ROOT_API
let timeout: number | undefined = undefined

const sliderItems = computed(() => articlePromotions.content ? articlePromotions.content : [])

const openURLAlert = async (): Promise<void> => {
    const alertParams = {
        message: 'Welchen Server möchtest du verwenden?',
        header: 'Server wechseln',
        cssClass: 'custom-alert',
        inputs: [
            {
                placeholder: 'API URL',
                cssClass: 'purple-purple-color background-color-light purple-purple-alert-input',
                value: 'https://api-v3.dev.ordersystems.net/api/'
            }
        ],
        buttons: [
            {
                text: 'Standard',
                handler: () => {
                    changeURL(standardURL)
                },
            },
            {
                text: 'OK',
                handler: (value: string[]) => {
                    changeURL(value[0])
                },
            }
        ],
    }

    const alert = await alertController.create(alertParams);
    await alert.present();
}

const startTimer = (): void => {
    timeout = setTimeout(() => {
        openURLAlert()
        timeout = undefined;
    }, 10000);
}

const endTimer = (): void => {
    if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
    }
}
</script>

<style scoped>
.start-slider {
    min-height: 280px;
    max-height: Max(calc(100vh - 460px), 280px);
}

@media (min-width: 1400px) {
    .start-slider.web-slider {
        clip-path: inset(0 100px 0 100px);
    }
}

.slide-card {
    -webkit-box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.65);
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
}

.inset-shadow {
    position: relative;
}

.inset-shadow::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 65.27%, #000000 100%);
    z-index: 2;
    border-radius: 16px;
}

.slide-card-text {
    bottom: 50px;
    left: 50%;
    width: max-content;
    max-width: 100%;
    z-index: 3;
}

@supports (translate: -50% 0) {
    .slide-card-text {
        translate: -50% 0;
    }
}

@supports not (translate: -50% 0) {
    .slide-card-text {
        transform: translate(-50%, 0);
    }
}
</style>

<style>
.background-color-light {
    background-color: var(--ion-color-light) !important;
}
</style>
